/**
 * SSCompanies — Tenant / Company Management
 * ✅ Refactored to use shared SS component library (components/ss).
 * API: ssCompanyAPI from ssApi.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssCompanyAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, SplitLayout, Section,
  Button,
  Spinner, EmptyState, ErrorBanner, SuccessBanner,
  DataTable, InfoGrid, StatBar, SearchBar,
  ActiveBadge,
  COLOR,
} from '../../components/ss';

// ── "Benevita Polar" form kit (local to this page) ───────────────────────────
const BP = {
  primary: '#7B1E3C', primaryLight: '#94335A', navy: '#2B1B22', teal: '#00897B',
  danger: '#D32F2F', muted: '#8b7681', border: '#ECE3E7', soft: '#f7f3f4',
  font: "'Inter', -apple-system, 'Segoe UI', sans-serif",
  grad: 'linear-gradient(135deg, #2B1B22, #7B1E3C)',
  gradBtn: 'linear-gradient(135deg, #7B1E3C, #94335A)',
};

const BPStyle = () => (
  <style>{`
    @keyframes bpFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
    .bp-fade { animation: bpFadeIn .2s ease both; }
    @media (prefers-reduced-motion: reduce) { .bp-fade { animation: none; } }
    .bp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    @media (max-width: 640px) { .bp-grid { grid-template-columns: 1fr; } }
  `}</style>
);

const bpInputBase = (focus) => ({
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13,
  fontFamily: BP.font, color: BP.navy, background: '#fff', borderRadius: 10,
  border: `1.5px solid ${focus ? BP.primary : '#ECE3E7'}`, outline: 'none',
  boxShadow: focus ? '0 0 0 4px rgba(123,30,60,.18)' : 'none',
  transition: 'border-color .15s, box-shadow .15s',
});

function BPInput({ style, onFocus, onBlur, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <input {...props}
      onFocus={e => { setFocus(true); onFocus && onFocus(e); }}
      onBlur={e => { setFocus(false); onBlur && onBlur(e); }}
      style={{ ...bpInputBase(focus), ...style }} />
  );
}

function BPSelect({ style, onFocus, onBlur, children, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <select {...props}
      onFocus={e => { setFocus(true); onFocus && onFocus(e); }}
      onBlur={e => { setFocus(false); onBlur && onBlur(e); }}
      style={{ ...bpInputBase(focus), ...style }}>{children}</select>
  );
}

const BPField = ({ label, required, children, style }) => (
  <label style={{ display: 'block', fontFamily: BP.font, ...style }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 5 }}>
      {label}{required && <span style={{ color: BP.danger }}> *</span>}
    </span>
    {children}
  </label>
);

const BPHeader = ({ icon, title, subtitle }) => (
  <div style={{ background: BP.grad, borderRadius: 14, padding: '16px 20px', color: '#fff',
    display: 'flex', alignItems: 'center', gap: 14, fontFamily: BP.font, marginBottom: 16 }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.14)',
      backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.22)', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' }}>{title}</div>
      <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>{subtitle}</div>
    </div>
  </div>
);

const BPSection = ({ icon, title, children }) => (
  <div style={{ background: BP.soft, borderRadius: 12, padding: 16, marginBottom: 14, fontFamily: BP.font }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: BP.navy, marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>{title}
    </div>
    {children}
  </div>
);

const BPButton = ({ children, disabled, ghost, onClick, type = 'button' }) => (
  <button type={type} disabled={disabled} onClick={onClick} style={{
    padding: '10px 22px', borderRadius: 10, fontSize: 13.5, fontWeight: 700, fontFamily: BP.font,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, transition: 'all .15s',
    ...(ghost
      ? { background: 'transparent', border: `1.5px solid ${BP.border}`, color: BP.muted }
      : { background: BP.gradBtn, border: 'none', color: '#fff', boxShadow: '0 2px 10px rgba(123,30,60,.30)' }),
  }}>{children}</button>
);

// ── Company form (create / edit) ──────────────────────────────────────────────
function CompanyForm({ initial, companies, onSubmit, onCancel, saving, err }) {
  const [form, setForm] = useState(
    initial || { name: '', shortname: '', tradingname: '', registrationno: '', taxno: '', countryid: '', parentcompanyid: '' }
  );
  const upd = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const isCreate = !initial;
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} style={{ fontFamily: BP.font }}>
      <BPStyle />
      <div className="bp-fade" style={isCreate ? { maxWidth: 860, margin: '0 auto' } : {}}>
        <BPHeader icon="🏢"
          title={isCreate ? 'Register a new company' : 'Edit company'}
          subtitle="Tenant agencies and provider companies operating on the platform" />

        <ErrorBanner>{err}</ErrorBanner>

        <BPSection icon="🏢" title="Company identity">
          <div className="bp-grid">
            <BPField label="Company Name" required style={{ gridColumn: '1 / -1' }}>
              <BPInput required value={form.name} onChange={upd('name')} placeholder="e.g. Nairobi Medical Center" />
            </BPField>
            <BPField label="Short Name">
              <BPInput value={form.shortname || ''} onChange={upd('shortname')} placeholder="e.g. NMC" />
            </BPField>
            <BPField label="Trading Name">
              <BPInput value={form.tradingname || ''} onChange={upd('tradingname')} placeholder="DBA / trading as" />
            </BPField>
          </div>
        </BPSection>

        <BPSection icon="📋" title="Registration & tax">
          <div className="bp-grid">
            <BPField label="Registration No.">
              <BPInput value={form.registrationno || ''} onChange={upd('registrationno')} />
            </BPField>
            <BPField label="Tax No.">
              <BPInput value={form.taxno || ''} onChange={upd('taxno')} />
            </BPField>
            <BPField label="Country Code">
              <BPInput value={form.countryid || ''} onChange={upd('countryid')} placeholder="e.g. KE" />
            </BPField>
          </div>
        </BPSection>

        <BPSection icon="🔗" title="Hierarchy">
          <BPField label="Parent Company (optional)">
            <BPSelect value={form.parentcompanyid || ''} onChange={upd('parentcompanyid')}>
              <option value="">None — top-level</option>
              {companies.filter(c => !initial || c.companyid !== initial.companyid).map(c => (
                <option key={c.companyid} value={c.companyid}>{c.name}</option>
              ))}
            </BPSelect>
          </BPField>
          <div style={{ fontSize: 11.5, color: BP.muted, marginTop: 6 }}>Leave empty for a root company</div>
        </BPSection>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <BPButton ghost onClick={onCancel}>Cancel</BPButton>
          <BPButton type="submit" disabled={saving}>
            {saving ? 'Saving…' : (initial ? 'Save changes ✓' : 'Create company ✓')}
          </BPButton>
        </div>
      </div>
    </form>
  );
}

// ── Company Detail panel ──────────────────────────────────────────────────────
function CompanyDetail({ company, companies, onEdit, onClose, onRefresh }) {
  const [ok, setOk]   = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { setOk(''); setErr(''); }, [company]);

  if (!company) return (
    <EmptyState icon="🏢" title="Select a company" message="Click any row to view details." />
  );

  const parent   = companies.find(c => c.companyid === company.parentcompanyid);
  const children = companies.filter(c => c.parentcompanyid === company.companyid);

  async function deactivate() {
    if (!window.confirm(`Deactivate "${company.name}"?`)) return;
    try { await ssCompanyAPI.deactivate(company.companyid); setOk('Company deactivated.'); onRefresh(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text }}>{company.name}</div>
          {company.tradingname && (
            <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>t/a {company.tradingname}</div>
          )}
          <div style={{ marginTop: 8 }}>
            <ActiveBadge active={company.isactive !== false} />
          </div>
        </div>
        <Button variant="ghost" onClick={onClose}>×</Button>
      </div>

      <ErrorBanner>{err}</ErrorBanner>
      <SuccessBanner>{ok}</SuccessBanner>

      <InfoGrid style={{ marginBottom: 18 }} rows={[
        ['Company ID',       company.companyid],
        ['Registration No.', company.registrationno],
        ['Tax No.',          company.taxno],
        ['Country',          company.countryid],
        ['Parent Company',   parent ? parent.name : 'None'],
        ['Created',          company.sys_entry_date ? new Date(company.sys_entry_date).toLocaleDateString() : null],
      ]} />

      {children.length > 0 && (
        <Section title={`Subsidiaries (${children.length})`} style={{ marginBottom: 18 }}>
          {children.map(c => (
            <div key={c.companyid} style={{
              padding: '6px 0', fontSize: 13, color: COLOR.textSub,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: `1px solid ${COLOR.graySubtle}`,
            }}>
              <span>{c.name}</span>
              <ActiveBadge active={c.isactive !== false} />
            </div>
          ))}
        </Section>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={() => onEdit(company)}>Edit</Button>
        {company.isactive !== false && (
          <Button variant="amber" onClick={deactivate}>Deactivate</Button>
        )}
      </div>
    </div>
  );
}

// ── Table columns ─────────────────────────────────────────────────────────────
const getCompanyCols = (companies) => [
  {
    key: 'name', label: 'Company',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 600, color: COLOR.text }}>{v}</div>
        {row.tradingname && <div style={{ fontSize: 11, color: COLOR.textMuted }}>t/a {row.tradingname}</div>}
      </div>
    ),
  },
  { key: 'registrationno', label: 'Registration', render: v => v || '—' },
  { key: 'countryid',      label: 'Country',       render: v => v || '—' },
  {
    key: 'parentcompanyid', label: 'Parent',
    render: v => {
      const p = companies.find(c => c.companyid === v);
      return p ? p.name : <span style={{ color: COLOR.grayLight }}>Root</span>;
    },
  },
  {
    key: 'isactive', label: 'Status',
    render: (_, row) => <ActiveBadge active={row.isactive !== false} />,
  },
];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [formErr, setFormErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    ssCompanyAPI.list()
      .then(d => { setCompanies(d.data || d || []); setErr(''); })
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(form) {
    setSaving(true); setFormErr('');
    try { await ssCompanyAPI.create(form); setShowCreate(false); load(); }
    catch (ex) { setFormErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  async function handleEdit(form) {
    setSaving(true); setFormErr('');
    try { await ssCompanyAPI.update(editTarget.companyid, form); setEditTarget(null); load(); setSelected(null); }
    catch (ex) { setFormErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  const filtered = companies.filter(c =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.tradingname?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Page>
      <PageHeader
        title="Companies"
        subtitle="Manage tenant organisations, subsidiaries and multi-tenancy setup"
        action={
          <button onClick={() => setShowCreate(true)} style={{
            padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700,
            fontFamily: BP.font, color: '#fff', border: 'none', cursor: 'pointer',
            background: BP.gradBtn, boxShadow: '0 2px 10px rgba(123,30,60,.30)',
          }}>+ New Company</button>
        }
      />

      <ErrorBanner>{err}</ErrorBanner>

      <StatBar style={{ marginBottom: 20 }} stats={[
        { label: 'Total',    value: companies.length,                              color: COLOR.blue },
        { label: 'Active',   value: companies.filter(c => c.isactive !== false).length, color: COLOR.greenDark },
        { label: 'Inactive', value: companies.filter(c => c.isactive === false).length, color: COLOR.redDark },
      ]} />

      <SplitLayout
        list={
          <Card>
            <SearchBar value={search} onChange={setSearch} placeholder="Search by name…" style={{ marginBottom: 14 }} />
            <DataTable
              columns={getCompanyCols(companies)}
              rows={filtered}
              rowKey="companyid"
              loading={loading}
              onRowClick={setSelected}
              selectedKey={selected?.companyid}
              emptyIcon="🏢"
              emptyTitle="No companies found"
            />
          </Card>
        }
        detail={
          <Card style={{ minHeight: 400 }}>
            <CompanyDetail
              company={selected}
              companies={companies}
              onEdit={c => setEditTarget(c)}
              onClose={() => setSelected(null)}
              onRefresh={() => { load(); setSelected(null); }}
            />
          </Card>
        }
      />

      <Modal open={showCreate} title="New Company" onClose={() => setShowCreate(false)} fullscreen>
        <CompanyForm companies={companies} saving={saving} err={formErr} onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />
      </Modal>
      <Modal open={!!editTarget} title="Edit Company" onClose={() => setEditTarget(null)}>
        <CompanyForm initial={editTarget} companies={companies} saving={saving} err={formErr} onSubmit={handleEdit} onCancel={() => setEditTarget(null)} />
      </Modal>
    </Page>
  );
}
